import React, { useState, useEffect } from 'react';
import { RichText } from '@sitecore-jss/sitecore-jss-react';
import { useSite } from '../../contexts/SiteContext';
import i18n from 'i18next';

/**
 * Helper function to extract site value from various field formats
 */
const getSiteValue = (siteField) => {
  if (!siteField) return '';
  
  if (typeof siteField === 'string') {
    return siteField;
  } else if (siteField?.url) {
    // Extract the last part of the URL path (e.g., "KSA" from "/Content/Settings/SiteOptions/KSA")
    const urlParts = siteField.url.split('/');
    return urlParts[urlParts.length - 1] || '';
  } else if (typeof siteField?.value === 'string') {
    return siteField.value;
  } else if (siteField?.value?.name) {
    return siteField.value.name;
  } else if (siteField?.name) {
    return siteField.name;
  } else if (siteField?.displayName) {
    return siteField.displayName;
  }
  return '';
};

/**
 * Footer component with site-specific text content.
 * Filters footerItems by site (KSA/UAE/ALL) and displays the matching one.
 */
const Footer = ({ fields }) => {
  const site = useSite();
  const [isClient, setIsClient] = useState(false);

  // Force re-render on client after hydration to get correct site context
  useEffect(() => {
    setIsClient(true);
  }, []);
  
  if (!fields) {
    return null;
  }

  const isRTL = i18n.language === 'ar' || i18n.language === 'ar-SA';
  const currentSite = site.isUAE ? 'UAE' : 'KSA';

  // Get footer items and filter by current site
  const footerItems = fields.footerItems || [];
  
  // Debug: log footer items and their site values
  console.log('[Footer] Current site:', currentSite, 'isUAE:', site.isUAE);
  footerItems.forEach((item, idx) => {
    console.log(`[Footer] Item ${idx}:`, item?.name || item?.displayName, 'site field:', item?.fields?.site, 'extracted:', getSiteValue(item?.fields?.site));
    console.log(`[Footer] Item ${idx} text:`, item?.fields?.text?.value?.substring(0, 50));
  });

  // First, try to find an exact match for current site
  let matchingItem = footerItems.find(item => {
    const itemSite = getSiteValue(item?.fields?.site);
    const isMatch = itemSite && itemSite.toUpperCase() === currentSite;
    console.log(`[Footer] Checking item site "${itemSite}" against "${currentSite}": ${isMatch}`);
    return isMatch;
  });

  console.log('[Footer] Exact match found:', matchingItem ? getSiteValue(matchingItem?.fields?.site) : 'none');

  // If no exact match, fall back to an item with 'ALL' or no site specified
  if (!matchingItem) {
    matchingItem = footerItems.find(item => {
      const itemSite = getSiteValue(item?.fields?.site);
      return !itemSite || itemSite.toUpperCase() === 'ALL';
    });
    console.log('[Footer] Fallback match:', matchingItem ? 'found' : 'none');
  }

  if (!matchingItem) {
    return null;
  }

  console.log('[Footer] Final selected item site:', getSiteValue(matchingItem?.fields?.site));
  console.log('[Footer] Final selected item text:', matchingItem?.fields?.text?.value?.substring(0, 50));

  // Get background color from the matching item or use default
  const bgColor = matchingItem.fields?.backgroundColor?.value || '#26247b';

  const footerStyle = {
    backgroundColor: bgColor,
    color: '#fff',
    padding: '20px',
    textAlign: 'center',
    marginTop: '40px',
    direction: isRTL ? 'rtl' : 'ltr',
  };

  // Style for links inside footer (email and phone)
  const footerLinkStyle = `
    .footer a {
      color: #fff;
      text-decoration: underline;
    }
    .footer a:hover {
      color: #e0e0e0;
    }
  `;

  return (
    <footer className="footer" style={footerStyle} key={`footer-${currentSite}-${isClient}`}>
      <style>{footerLinkStyle}</style>
      {matchingItem.fields?.text && (
        <RichText field={matchingItem.fields.text} />
      )}
    </footer>
  );
};

export default Footer;
